import type { TFunction } from "i18next";
import i18n from "@/i18n/index";

/**
 * Locale-format any numeric interpolation params so credit figures in a
 * translated message read grouped (e.g. "100,000") rather than as a bare
 * integer. Non-numeric params pass through unchanged.
 */
function formatParams(
  params: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      typeof value === "number" && Number.isFinite(value)
        ? value.toLocaleString()
        : value,
    ]),
  );
}

/**
 * Resolve the displayed message via i18n when a translation key (tk) is available.
 *
 * When the backend supplies `params` alongside the key (e.g. a spend-denied
 * billing error), they are interpolated into the message so it reads with the
 * real figures.
 *
 * Falls back to the literal message when:
 * - tk is missing
 * - the key is missing from every loaded locale (i18next returns the key itself;
 *   { defaultValue } substitutes the literal message instead)
 */
export function resolveMessage(
  tk: string | undefined,
  message: string,
  params?: Record<string, unknown>,
  translate: TFunction = i18n.t,
): string {
  if (!tk) return message;
  return translate(tk, { defaultValue: message, ...formatParams(params) });
}

interface DisplayError {
  tk?: string;
  code?: string;
  message?: string;
  params?: Record<string, unknown>;
}

/** Keep the history category concise; put localized guidance in the message. */
export function resolveErrorDisplay(
  error: DisplayError | undefined,
  translate: TFunction,
) {
  const message = resolveMessage(
    error?.tk,
    error?.message || translate("errors.generic"),
    error?.params,
    translate,
  );
  let label: string;
  if (error?.code) {
    label = error.code
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  } else if (!error?.message && !error?.tk) {
    label = translate("errors.failed");
  } else {
    label = message.length > 30 ? `${message.slice(0, 30)}…` : message;
  }
  return { message, label };
}
